import { EMPTY_TEXT } from '../formatHelpers';

export interface ReiphisDataItem {
  cropId?: string;
  cropName?: string;
  source?: string;
  rei?: number | string;
  reiu?: string;
  phi?: number | string;
  phiu?: string;
}

const normalizeCropId = (cropId?: string) =>
  cropId?.replace(/-/gi, '').toLowerCase() ?? '';

// Resolve the reiphis entries that apply to the application's crops.
//
// Primary match is by cropId: a user-edited (CWF) override is saved keyed by
// cropId and comes back from the proxy with no cropName, and its cropId can
// differ from the manufacturer (MASTERLIST) entry that merely shares a similar
// name. Matching by the application crop's id is the only reliable way to
// surface the override — matching by name finds the wrong record.
//
// When no cropId is available (e.g. older data), fall back to the legacy
// name-based match across ALL crop names, then include siblings by cropId so
// a CWF override that shares the cropId but lacks a cropName is still included.
const getMatchingReiphisData = (
  reiphisData: ReiphisDataItem[],
  cropNames: string[],
  cropIds: string[]
): ReiphisDataItem[] => {
  const normalizedCropIds = new Set(
    cropIds.map(normalizeCropId).filter(Boolean)
  );

  if (normalizedCropIds.size) {
    const byCropId = reiphisData.filter(data =>
      normalizedCropIds.has(normalizeCropId(data.cropId))
    );
    if (byCropId.length) {
      return byCropId;
    }
  }

  const uniqueCropNames = [...new Set(cropNames)].filter(Boolean);

  const directMatches = reiphisData.filter(data => {
    const dataCropName = data.cropName?.toLowerCase();
    return uniqueCropNames.some(cropName =>
      dataCropName?.includes(cropName.toLowerCase())
    );
  });

  if (!directMatches.length) {
    return [];
  }

  // Include CWF overrides that share a cropId with a direct match but carry no cropName.
  const matchedCropIds = new Set(
    directMatches.map(d => normalizeCropId(d.cropId)).filter(Boolean)
  );
  const siblingsOnly = matchedCropIds.size
    ? reiphisData.filter(
        d =>
          matchedCropIds.has(normalizeCropId(d.cropId)) &&
          !directMatches.includes(d)
      )
    : [];

  return [...directMatches, ...siblingsOnly];
};

// Group the matched entries by crop identity (cropId when present, else name).
const groupByCrop = (crops: ReiphisDataItem[]): ReiphisDataItem[][] => {
  const groups = crops.reduce<Record<string, ReiphisDataItem[]>>(
    (acc, crop) => {
      const key =
        normalizeCropId(crop.cropId) ||
        crop.cropName?.toLowerCase() ||
        'unknown';
      acc[key] = acc[key] || [];
      acc[key].push(crop);
      return acc;
    },
    {}
  );
  return Object.values(groups);
};

// For one crop and one field, prefer the user override (CWF) then the
// manufacturer (MASTERLIST), ignoring any other source. The field value is
// required, so a CWF override that edits only one field (e.g. PHI) still falls
// back to the MASTERLIST value for the field it left unset.
const resolveField = (
  group: ReiphisDataItem[],
  valueKey: 'rei' | 'phi'
): ReiphisDataItem | undefined => {
  const fromSource = (source: string) =>
    group.find(d => d.source === source && d[valueKey] != null);
  return fromSource('CWF') ?? fromSource('MASTERLIST');
};

// Normalise to hours so values with different units (hours vs days) are comparable.
const toHours = (
  value: number | string | undefined,
  unit: string | undefined
): number => {
  const num =
    typeof value === 'string' ? Number.parseFloat(value) : value ?? Number.NaN;
  if (!Number.isFinite(num)) return Infinity;
  return unit?.toLowerCase().startsWith('d') ? num * 24 : num;
};

// Across all crops, find the entry with the smallest value for the given field.
const getMinEntry = (
  cropGroups: ReiphisDataItem[][],
  valueKey: 'rei' | 'phi',
  unitKey: 'reiu' | 'phiu'
): ReiphisDataItem | undefined =>
  cropGroups
    .map(group => resolveField(group, valueKey))
    .filter((entry): entry is ReiphisDataItem => entry != null)
    .reduce<ReiphisDataItem | undefined>((min, curr) => {
      if (!min) return curr;
      return toHours(curr[valueKey], curr[unitKey]) <
        toHours(min[valueKey], min[unitKey])
        ? curr
        : min;
    }, undefined);

const formatValueWithUnit = (value: unknown, unit: string): string => {
  const separator = String(value).length >= 2 ? '\n' : '';
  return `${value}${separator}${unit}`;
};

export const getReiPhiTexts = (
  reiphisData: ReiphisDataItem[] | undefined,
  cropNames: string[],
  cropIds: string[] = []
): { reiText: string; phiText: string } => {
  let reiText = EMPTY_TEXT;
  let phiText = EMPTY_TEXT;

  if (!reiphisData?.length) {
    return { reiText, phiText };
  }

  const matchingCrops = getMatchingReiphisData(reiphisData, cropNames, cropIds);

  if (!matchingCrops.length) {
    return { reiText, phiText };
  }

  // When the operation spans multiple crop zones, regulations require displaying
  // the most restrictive (smallest) value for each field independently.
  const cropGroups = groupByCrop(matchingCrops);
  const minReiEntry = getMinEntry(cropGroups, 'rei', 'reiu');
  const minPhiEntry = getMinEntry(cropGroups, 'phi', 'phiu');

  if (minReiEntry?.rei != null) {
    const reiUnit = minReiEntry.reiu?.charAt(0).toLowerCase() || '';
    reiText = formatValueWithUnit(minReiEntry.rei, reiUnit);
  }

  if (minPhiEntry?.phi != null) {
    const phiUnit = minPhiEntry.phiu?.charAt(0).toLowerCase() || '';
    phiText = formatValueWithUnit(minPhiEntry.phi, phiUnit);
  }

  return { reiText, phiText };
};
